import "./style.sass";
import assets from "../../assets";
import { ROUTE_ADMIN_QUIZ_ROOM } from "../../route/teacherRoute";
import { useHandler } from "./handler";
import { useEffect, useState, useRef } from "react";

const formatDuration = (time) => {
  if (!time) return "0 min";

  const [hours, minutes] = time.split(":").map(Number);

  const totalMinutes = hours * 60 + minutes;

  return `${totalMinutes} min`;
};

const formatMinutesToHoursAndMins = (minutes) => {
  if (minutes === null || minutes === undefined || minutes === "" || isNaN(minutes)) {
    return "--";
  }
  const mins = Number(minutes);
  if (mins <= 0) return "--";

  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;

  if (hrs > 0 && remMins > 0) {
    return `${hrs} hr${hrs > 1 ? "s" : ""} ${remMins} min${remMins > 1 ? "s" : ""}`;
  } else if (hrs > 0) {
    return `${hrs} hr${hrs > 1 ? "s" : ""}`;
  } else {
    return `${remMins} min${remMins > 1 ? "s" : ""}`;
  }
};

const formatLabel = (value) => {
  return value
    ?.split("_")
    ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    ?.join(" ");
};

const CustomSelect = ({
  name,
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  searchable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);
  const displayLabel = selectedOption ? selectedOption.label : placeholder;

  const filteredOptions = searchable && search.trim() !== ""
    ? options.filter((opt) => (opt.label || "").toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <div className="custom_async_select_container" ref={dropdownRef}>
      <button
        type="button"
        className={`custom_async_select_trigger ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={!selectedOption ? "placeholder" : ""}>{displayLabel}</span>
        <svg
          className={`custom_async_chevron ${isOpen ? "rotate" : ""}`}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#6B7280"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div className="custom_async_select_menu">
          {searchable && (
            <div className="custom_async_search_box">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search test name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
          )}
          <div className="custom_async_select_options">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt, idx) => {
                const isSelected = opt.value === value;
                return (
                  <div
                    key={opt.value || idx}
                    className={`custom_async_option_item ${isSelected ? "selected" : ""}`}
                    onClick={() => {
                      onChange({ target: { name, value: opt.value } });
                      setIsOpen(false);
                      setSearch("");
                    }}
                  >
                    <span>{opt.label}</span>
                    {isSelected && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#612EF7" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="custom_async_no_options">No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AsyncTestModal = ({
  open,
  onClose,
  testName,
  studentTier,
  tests,
  tiers,
  onChange,
  onApply,
  mode,
  testDetails,
  tierDetails,
  isApplying,
}) => {
  useEffect(() => {
    if (open) {
      onChange({ target: { name: "test_name", value: "" } });
      onChange({ target: { name: "student_tier", value: "" } });
    }
  }, [open]);

  if (!open) return null;

  const testOptions = (testDetails || [])
    .map((item) => ({
      value: item?.test_name || "",
      label: item?.test_name || "",
    }))
    .filter((opt) => opt.value);

  const formattedTiers = (tierDetails || []).map((item) => ({
    value: formatLabel(item),
    label: formatLabel(item),
  }));

  const filteredTiers = formattedTiers.filter(
    (t) => (t.label || "").trim().toLowerCase() !== "all"
  );

  const tierOptions = [
    { value: "All", label: "All" },
    ...filteredTiers,
  ];

  return (
    <div className="async_modal_overlay" onClick={onClose}>
      <div className="async_modal" onClick={(e) => e.stopPropagation()}>
        <div className="async_modal_header">
          <div>
            <h2>Create {mode === "live" ? "Live Test" : "Async Test"}</h2>

            <p>
              Create an {mode === "live" ? "live test " : "asynchronous test "}
              that students can attempt anytime using a test code.
            </p>
          </div>

          <button className="async_close_btn" onClick={onClose} disabled={isApplying}>
            ×
          </button>
        </div>

        <div className="async_modal_body">
          <div className="async_input_wrapper">
            <label>Select Test Name</label>
            <CustomSelect
              name="test_name"
              value={testName}
              onChange={onChange}
              options={testOptions}
              placeholder="Select test name"
              searchable={true}
            />
          </div>

          <div className="async_input_wrapper">
            <label>Select Student Tier</label>
            <CustomSelect
              name="student_tier"
              value={studentTier}
              onChange={onChange}
              options={tierOptions}
              placeholder="Select tier"
              searchable={false}
            />
          </div>
        </div>

        <div className="async_modal_footer">
          <button className="async_cancel_btn" onClick={onClose} disabled={isApplying}>
            Cancel
          </button>

          <button
            className="async_apply_btn"
            disabled={!testName || !studentTier || isApplying}
            onClick={onApply}
          >
            {isApplying ? "Applying..." : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const AddStudentsModal = ({
  open,
  onClose,
  students,
  searchQuery,
  setSelectedStudents,
  selectedStudents,
  setSearchQuery,
  addStudentEmail,
  isLoading,
}) => {
  const [errorMessage, setErrorMessage] = useState("");

  // ✅ Hook should always execute
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setSelectedStudents([]);
      setErrorMessage("");
    }
  }, [open, setSearchQuery, setSelectedStudents]);

  // ✅ Conditional return AFTER hooks
  if (!open) return null;

  const handleAttemptClose = () => {
    if (!selectedStudents || selectedStudents.length === 0) {
      setErrorMessage("Please select the student");
      return;
    }
    setErrorMessage("");
    onClose();
  };

  const getStudentEmail = (s) => s?.email || s?.user_mail || s?.user_email || s?.name || "";

  const filteredStudents = students?.filter((student) => {
    const name = student?.name || "";
    const email = getStudentEmail(student);

    return (
      name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const isAllSelected =
    filteredStudents?.length > 0 &&
    filteredStudents?.every((student) =>
      selectedStudents?.includes(getStudentEmail(student)),
    );

  const handleSelectStudent = (email) => {
    setErrorMessage("");
    setSelectedStudents((prev) =>
      prev.includes(email)
        ? prev.filter((item) => item !== email)
        : [...prev, email],
    );
  };

  const handleSelectAll = (checked) => {
    setErrorMessage("");
    if (checked) {
      setSelectedStudents((prev) => [
        ...new Set([
          ...prev,
          ...filteredStudents?.map((student) => getStudentEmail(student)),
        ]),
      ]);
    } else {
      const filteredEmails = filteredStudents?.map((student) => getStudentEmail(student));

      setSelectedStudents((prev) =>
        prev.filter((email) => !filteredEmails.includes(email)),
      );
    }
  };

  const handleAddStudents = async () => {
    if (!selectedStudents || selectedStudents.length === 0) {
      setErrorMessage("Please select the student");
      return;
    }
    // API call
    await addStudentEmail(selectedStudents);
  };

  return (
    <div className="async_modal_overlay" onClick={handleAttemptClose}>
      <div
        className="async_modal add_students_modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="async_modal_header">
          <div>
            <h2>Add Students</h2>

            <p>
              Create an asynchronous test that students can attempt anytime
              using a test code.
            </p>
          </div>

          <button className="async_close_btn" onClick={handleAttemptClose}>
            ×
          </button>
        </div>

        {errorMessage && (
          <div className="student_select_error_banner">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Search */}
        <div className="student_search_wrapper">
          <span>⌕</span>

          <input
            type="text"
            placeholder="Search Student Name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Student List */}
        <div className="student_list_wrapper">
          <div
            className="student_list_header"
            onClick={() => handleSelectAll(!isAllSelected)}
            style={{ cursor: "pointer" }}
          >
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={(e) => handleSelectAll(e.target.checked)}
              onClick={(e) => e.stopPropagation()}
            />

            <span>Student Name</span>
          </div>

          {filteredStudents?.length > 0 ? (
            filteredStudents?.map((student, index) => {
              const email = getStudentEmail(student);
              const isSelected = selectedStudents.includes(email);

              return (
                <div
                  className="student_list_item"
                  key={email || index}
                  onClick={() => handleSelectStudent(email)}
                  style={{ cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleSelectStudent(email)}
                    onClick={(e) => e.stopPropagation()}
                  />

                  <span>{student?.name || email || "-"}</span>
                </div>
              );
            })
          ) : (
            <div className="no_students">No students found</div>
          )}
        </div>

        {/* Footer */}
        <div className="add_students_footer">
          <button className="async_cancel_btn" onClick={handleAttemptClose}>
            <span>Cancel</span>
          </button>

          <button
            className="async_apply_btn"
            disabled={selectedStudents?.length === 0}
            onClick={handleAddStudents}
          >
            {isLoading ? (
              <section className="publish_spinner"></section>
            ) : (
              <span> Add Students ({selectedStudents?.length})</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export const TestCreatedSuccessModal = ({
  open,
  details: rawDetails,
  onClose,
  onGoManagement,
  onPublish,
  isLoading,
}) => {
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const details = rawDetails?.data || rawDetails || {};
  const testName = details?.test_name || details?.testName || "Assessment Test";
  const roomCode = details?.room_code || details?.roomCode || "";
  const scheduledDate = details?.scheduled_date || details?.scheduledDate || "--";
  const duration = details?.duration_minutes ?? details?.duration ?? "--";
  const totalQuestions = details?.total_questions ?? details?.questions ?? "--";
  const totalMarks = details?.total_marks ?? details?.marks ?? "--";
  const assignedStudents = details?.total_assigned_students ?? details?.added ?? "--";

  const handleCopy = () => {
    if (roomCode) {
      navigator.clipboard.writeText(roomCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="success_modal_overlay" onClick={onClose}>
      <div className="success_modal" onClick={(e) => e.stopPropagation()}>
        {/* Close */}
        <button className="success_close" onClick={onClose} aria-label="Close">
          ×
        </button>

        {/* Success Icon */}
        <div className="success_icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>

        <h2>Test Created Successfully!</h2>

        <p className="success_description">
          Your test has been created and is ready to be published.
        </p>

        {/* Professional Details Card */}
        <div className="success_card">
          {/* Header Row: Test Name */}
          <div className="success_card_header">
            <span className="card_label">Test Name</span>
            <strong className="card_title">{testName}</strong>
          </div>

          {/* Room Code Hero Box */}
          <div className="room_code_box">
            <div className="room_code_info">
              <span className="room_code_label">ROOM CODE</span>
              <strong className="room_code_value">{roomCode}</strong>
            </div>
            <button
              type="button"
              className={`copy_btn ${copied ? "copied" : ""}`}
              onClick={handleCopy}
            >
              {copied ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                  Copy Code
                </>
              )}
            </button>
          </div>

          {/* Key Metrics Grid */}
          <div className="stats_grid">
            <div className="stat_item">
              <span className="stat_label">Scheduled Date</span>
              <span className="stat_value">{scheduledDate}</span>
            </div>
            <div className="stat_item">
              <span className="stat_label">Duration</span>
              <span className="stat_value">{formatMinutesToHoursAndMins(duration)}</span>
            </div>
            <div className="stat_item">
              <span className="stat_label">Total Questions</span>
              <span className="stat_value">{totalQuestions}</span>
            </div>
            <div className="stat_item">
              <span className="stat_label">Total Marks</span>
              <span className="stat_value">{totalMarks}</span>
            </div>
          </div>

          {/* Additional Info Row: Assigned Students */}
          <div className="card_footer_row">
            <span className="footer_label">Assigned Students</span>
            <span className="footer_value_badge">{assignedStudents} Student(s)</span>
          </div>
        </div>

        {/* Info Note */}
        <div className="success_info">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6731df" strokeWidth="2" style={{ flexShrink: 0, marginTop: "2px" }}>
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          <p>
            Once published, students will be able to see and join the test as
            per the scheduled date and time.
          </p>
        </div>

        {/* Footer Actions */}
        <div className="success_footer">
          <button className="cancel" onClick={onGoManagement}>
            Go to Test Management
          </button>

          <button className="publish" onClick={onPublish} disabled={isLoading}>
            {isLoading ? (
              <section className="publish_spinner"></section>
            ) : (
              <span>Publish Test Now</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const getPageNumbers = (currentPage, totalPages) => {
  if (!totalPages || totalPages <= 0) return [1];
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "...", totalPages];
  }
  if (currentPage >= totalPages - 3) {
    return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
};

export const TestManagement = () => {
  const [page, setPage] = useState(1);

  const {
    showAsyncModal,
    roomDetails,
    testDetails,
    tierDetails,
    studentMailDetails,
    formData,
    showAddStudentsModal,
    testMode,
    showSuccessModal,
    createdTest,
    newTableData,
    activeTab,
    searchQuery,
    selectedStudents,
    studentCodeDetails,
    currentPage,
    isLoading,
    isApplying,
    getApiNewTableDatas,
    setSearchQuery,
    setSelectedStudents,
    // getRoomDetails,
    onChangeValue,
    setShowAsyncModal,
    setShowAddStudentsModal,
    setTestMode,
    setShowSuccessModal,
    setActiveTab,
    applyTestDetails,
    apiTestRoomCodeStudents,
    apiTestOnPublish,
    navigate,
  } = useHandler();

  const { test_name, student_tier } = formData;

  const filteredData =
    activeTab === "all"
      ? newTableData
      : newTableData?.filter(
          (item) => item.type?.toLowerCase() === activeTab.toLowerCase(),
        );

  const ITEMS_PER_PAGE = 5;

  const start = (page - 1) * ITEMS_PER_PAGE;
  const end = start + ITEMS_PER_PAGE;

  const paginatedData = filteredData?.slice(start, end);

  const totalPages = Math.ceil((filteredData?.length || 0) / ITEMS_PER_PAGE);

  const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;

  const endItem = Math.min(currentPage * ITEMS_PER_PAGE, paginatedData?.length);

  return (
    <section className="test-management">
      {/* top cards */}
      <section className="test-header">
        <h2>Test Management</h2>
        <p className="test-create">
          Create and manage tests & quizzes for your students
        </p>
      </section>
      <section className="mode-container">
        <section className="mode-wrapper async">
          <section className="icon">
            <img src={assets.ic_async} alt="async" />
            <section className="mode-card">
              <h3>Async</h3>
              <p>
                Create a test with a code. Students can attempt it anytime,
                anywhere at their convenience.
              </p>
              <button
                // onClick={() =>
                //   navigate(ROUTE_TEACHER_QUIZ, {
                //     state: { mode: "async" },
                //   })
                // }
                onClick={() => {
                  setTestMode("async");
                  setShowAsyncModal(true);
                }}
              >
                Create Async Test
              </button>
            </section>
          </section>
        </section>

        <section className="mode-wrapper live">
          <section className="icon">
            <img src={assets.ic_live} alt="async" />
            <section className="mode-card">
              <h3>Live</h3>
              <p>
                Create a live room with a code. Students join the room and
                attempt the test together in real-time.
              </p>
              <button
                // onClick={() =>
                //   navigate(ROUTE_TEACHER_QUIZ, {
                //     state: { mode: "live" },
                //   })
                // }
                onClick={() => {
                  setTestMode("live");
                  setShowAsyncModal(true);
                }}
              >
                Create Live Test
              </button>
            </section>
          </section>
        </section>
      </section>

      {/* table section */}

      <section className="table-box">
        <section className="table-header">
          <section className="tabs">
            <span
              className={activeTab === "all" ? "active" : ""}
              onClick={() => {
                setActiveTab("all");
                setPage(1);
              }}
            >
              All Test
            </span>

            <span
              className={activeTab === "async" ? "active" : ""}
              onClick={() => {
                setActiveTab("async");
                setPage(1);
              }}
            >
              <img
                src={
                  activeTab === "async"
                    ? assets?.ic_time_white
                    : assets.ic_timer_grey
                }
                alt=""
              />{" "}
              Async Test
            </span>

            <span
              className={activeTab === "live" ? "active" : ""}
              onClick={() => {
                setActiveTab("live");
                setPage(1);
              }}
            >
              <img
                src={
                  activeTab === "live"
                    ? assets.ic_live_test_white
                    : assets.ic_live_test_grey
                }
                alt=""
              />{" "}
              Live Test
            </span>
          </section>
        </section>

        <table>
          <thead>
            <tr>
              <th>Room Code</th>
              <th>Test Name</th>
              <th>Type</th>
              <th>Category</th>
              <th>Exam</th>
              <th>Class</th>
              <th>Scheduled On</th>
              <th>Duration</th>
              <th>Status</th>

              {/* <th>Points</th> */}
            </tr>
          </thead>

          <tbody>
            {paginatedData?.length > 0 ? (
              paginatedData?.map((item, index) => (
                <tr key={index}>
                  <td>
                    <h4
                      className={
                        item.type?.toLowerCase() === "async"
                          ? "hyper_link_test"
                          : "not_hyper_link"
                      }
                      onClick={() => {
                        if (item.type?.toLowerCase() === "async")
                          navigate(ROUTE_ADMIN_QUIZ_ROOM, {
                            state: {
                              mode: item?.type?.toLowerCase() || "async",
                              roomCode: item?.room_code,
                              testName: item?.test_name,
                              test_name: item?.test_name,
                            },
                          });
                      }}
                    >
                      {item.room_code}
                    </h4>
                  </td>
                  <td
                    style={{ cursor: item.type?.toLowerCase() === "async" ? "pointer" : "default" }}
                    onClick={() => {
                      if (item.type?.toLowerCase() === "async")
                        navigate(ROUTE_ADMIN_QUIZ_ROOM, {
                          state: {
                            mode: item?.type?.toLowerCase() || "async",
                            roomCode: item?.room_code,
                            testName: item?.test_name,
                            test_name: item?.test_name,
                          },
                        });
                    }}
                  >
                    {item.test_name}
                  </td>

                  <td>
                    <section
                      className={`type-badge ${
                        item.type?.toLowerCase() === "async" ? "async" : "live"
                      }`}
                    >
                      <img
                        src={
                          item.type?.toLowerCase() === "async"
                            ? assets.ic_async_status
                            : assets.ic_live_status
                        }
                        alt="async"
                      />{" "}
                      {item.type}
                    </section>
                  </td>

                  <td>{item.category}</td>
                  <td>{item.exam}</td>
                  <td>{item.clas}</td>
                  <td>{item.scheduled_date}</td>
                  <td>{formatMinutesToHoursAndMins(item?.duration_minutes)}</td>
                  {/* <td>{formatDuration(item?.scheduled_time)}</td> */}
                  <td>
                    <h4 className={`test_table_status ${item?.status}`}>
                      {item?.status === "completed"
                        ? "Completed"
                        : item?.status === "not_started"
                          ? "Not Started"
                          : "On going"}
                    </h4>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="no_data_found_table">
                  No Data Found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="pagination_container">
          <div className="pagination_info">
            Showing {startItem} to {endItem} of {filteredData?.length} tests
          </div>

          <div className="pagination">
            <button
              className="page_btn_wrapper"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              ‹
            </button>

            {getPageNumbers(page, totalPages).map((p, idx) =>
              p === "..." ? (
                <span key={`ellipsis-${idx}`} className="pagination_ellipsis">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  className={`page_btn_wrapper ${page === p ? "active" : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              )
            )}

            <button
              className="page_btn_wrapper"
              disabled={page === totalPages || totalPages === 0}
              onClick={() => setPage(page + 1)}
            >
              ›
            </button>
          </div>
        </div>
      </section>

      {/* async model */}
      {showAsyncModal && (
        <AsyncTestModal
          open={showAsyncModal}
          onClose={() => !isApplying && setShowAsyncModal(false)}
          testName={test_name}
          studentTier={student_tier}
          onChange={onChangeValue}
          onApply={async () => {
            const success = await applyTestDetails();
            if (success) {
              setShowAddStudentsModal(true);
              setShowAsyncModal(false);
            }
          }}
          mode={testMode}
          testDetails={testDetails}
          tierDetails={tierDetails}
          isApplying={isApplying}
        />
      )}

      {showAddStudentsModal && (
        <AddStudentsModal
          open={showAddStudentsModal}
          onClose={() => setShowAddStudentsModal(false)}
          students={studentMailDetails}
          setSearchQuery={setSearchQuery}
          searchQuery={searchQuery}
          setSelectedStudents={setSelectedStudents}
          selectedStudents={selectedStudents}
          addStudentEmail={apiTestRoomCodeStudents}
          isLoading={isLoading}
        />
      )}

      {showSuccessModal && (
        <TestCreatedSuccessModal
          open={showSuccessModal}
          details={studentCodeDetails}
          onClose={async () => {
            setShowSuccessModal(false);
            await getApiNewTableDatas();
          }}
          onGoManagement={async () => {
            setShowSuccessModal(false);
            await getApiNewTableDatas();
          }}
          onPublish={async () => {
            await apiTestOnPublish();
          }}
          isLoading={isLoading}
        />
      )}
    </section>
  );
};
